import { createEnv } from '@t3-oss/env-core';
import { z } from 'zod';

const zEnvBool = z
  .string()
  .optional()
  .transform(val => {
    if (!val) return false;
    return val.toLowerCase() === 'true';
  });

const EnvSchema = z.object({
  REACT_APP_ENV: z.enum(['dev', 'test', 'prod']),
  REACT_APP_OKTA_CLIENT_ID: z.string(),
  REACT_APP_OKTA_ISSUER: z.string(),
  REACT_APP_PATHWAY_API_URL: z.url(),
  REACT_APP_PATHWAY_CDN_URL: z.url(),
  REACT_APP_PATHWAY_LOGOUT_REDIRECT: z.url().optional(),

  REACT_APP_ROUTE_LOGGING_ENABLED: zEnvBool,
  REACT_APP_REDUX_DEVTOOLS_TRACING_ENABLED: zEnvBool,
  REACT_APP_VERSION: z.string().optional(),
  REACT_APP_XP_API_IMAGE_URL_DEV: z.string().optional(),
  REACT_APP_XP_API_IMAGE_URL_TEST: z.string().optional(),
  REACT_APP_XP_API_IMAGE_URL_PROD: z.string().optional(),
  REACT_APP_XP_API_URL: z.url(),
  REACT_APP_LAUNCHDARKLY_CLIENT_ID: z.string(),
  REACT_APP_AMPLITUDE_API_KEY: z.string(),

  REACT_APP_CFA_HOME_URL: z.url().optional(),
  REACT_APP_SUPPORT_LINK: z.url(),
  REACT_APP_PATHWAY_NATIVE_API_URL: z.url(),
  REACT_APP_XP_API_IMAGE_URL: z.url(),
  REACT_APP_PATHWAY_XP_API_URL: z.url().optional(),
  REACT_APP_DATADOG_APP_ID: z.string().optional(),
  REACT_APP_PUBLIC_DATADOG_CLIENT_TOKEN: z.string().optional(),
  REACT_APP_ENABLE_REDUX_DEVTOOLS_TRACING_ENABLED: zEnvBool,
  REACT_APP_OKTA_BASE_URL: z.url().optional(),
});

const isCypress =
  typeof globalThis !== 'undefined' &&
  !!(globalThis as Record<string, unknown>).Cypress;

export const env = createEnv({
  // @TODO: Can't get build-time resolution of REACT_APP_ vars in cypress.
  // For now, we can just skip validation in cypress..
  skipValidation: isCypress,
  client: EnvSchema.shape,

  /**
   * The prefix that client-side variables must have. This is enforced both at
   * a type-level and at runtime.
   */
  clientPrefix: 'REACT_APP_',

  /**
   * What object holds the environment variables at runtime. This is usually
   * `process.env` or `import.meta.env`.
   */
  runtimeEnv: import.meta.env,

  /**
   * By default, this library will feed the environment variables directly to
   * the Zod validator.
   *
   * This means that if you have an empty string for a value that is supposed
   * to be a number (e.g. `PORT=` in a ".env" file), Zod will incorrectly flag
   * it as a type mismatch violation. Additionally, if you have an empty string
   * for a value that is supposed to be a string with a default value (e.g.
   * `DOMAIN=` in an ".env" file), the default value will never be applied.
   *
   * In order to solve these issues, we recommend that all new projects
   * explicitly specify this option as true.
   */
  emptyStringAsUndefined: true,
  onValidationError: issues => {
    // issues is an array of ZodIssue objects
    // throw with a JSON string so you see exactly what's missing / wrong
    throw new Error(
      '❌ Invalid environment variables:\n' + JSON.stringify(issues, null, 2),
    );
  },
});
